import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Pencil } from "lucide-react";

import { Button } from "../../../../components";
import FormHeader from "../../../../components/form/FormHeader";
import { useToast } from "../../../../components/toast-container";
import EventEditor, { type EventEditorSubmitValues } from "../components/event-form/EventEditor";
import { useGetDetailEvent, useUpdateEvent } from "../hooks";
import type { UpdateEventForAdminRequest } from "../types/event.types";
import { buildEventDateTimeFields } from "../../../../lib/date-time";
import LeftArrow from "../../../../assets/icons/left-arrow-gray-small.svg";
import _ from "lodash";
import { goBackOrGoHome } from "../../../../utils/navigation";

type EventUpdatePageProps = {
  basePath?: string;
};

export default function EventUpdatePage({ basePath = "/event" }: EventUpdatePageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { updateEvent, loading: isSubmitting } = useUpdateEvent();
  const { addToast } = useToast();
  const { event, loading: isLoadingEvent, error: eventError } = useGetDetailEvent(id);
  const canUpdateEvent = event?.event_status === "upcoming" || event?.event_status === "ongoing";
  const formId = "event-update-form";

  const handleUpdateEvent = async (values: EventEditorSubmitValues) => {
    if (!event || !id) {
      throw new Error("Missing event data for update");
    }

    const { startTime, endTime, eventDateTimeOffset, checkInTimeOffset } = buildEventDateTimeFields(
      values.eventDate,
      values.start,
      values.end,
      values.checkInTime,
      values.timezone,
    );

    const submitRequest: UpdateEventForAdminRequest = {
      title: values.name,
      description: values.description,
      latitude: Number(values.lat),
      longitude: Number(values.lon),
      radius: Number(values.radius),
      event_date: eventDateTimeOffset,
      start_time: startTime,
      end_time: endTime,
      check_in_time: checkInTimeOffset || undefined,
      guest_attendance_verification: values.guestAttendanceVerification || undefined,
      location: values.location,
      points: values.points ? Number(values.points) : undefined,
      event_image_file: values.image ?? undefined,
      image_url: values.imageUrl || undefined,
      timezone: values.timezone,
      category_id: values.categoryId || undefined,
      interest_ids: values.subCategoryIds,
      sub_category_ids: values.subCategoryIds,
    };

    await updateEvent(event.id, submitRequest);
    addToast({
      type: "success",
      title: "Success!",
      message: "Event has been updated successfully",
    });
    navigate(basePath);
  };

  if (_.isNil(event)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
          <p className="text-sm text-gray-600 dark:text-gray-300">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (eventError && !event) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <p className="mb-4 text-sm text-red-600 dark:text-red-400">{eventError}</p>
          <Button
            onClick={() => navigate(basePath)}
            type="button"
          >
            {t("common.back")}
          </Button>
        </div>
      </div>
    );
  }

  if (event && !canUpdateEvent) {
    return (
      <Navigate
        to={`${basePath}/edit/${event.id}`}
        replace
      />
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900">
      <FormHeader
        onBack={() => goBackOrGoHome(navigate)}
        title={t("event.edit_event")}
        titleIcon={<Pencil className="h-5 w-5" aria-hidden="true" />}
        subtitle=""
        backLabel={t("common.back")}
        backIcon={<img src={LeftArrow} alt="" className="dark:invert" />}
        actions={
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-10 rounded-md"
              onClick={() => navigate(`${basePath}/edit/${event.id}`)}
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="default"
              className="rounded-md bg-emerald-500 text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Updating..." : "Update"}
            </Button>
          </div>
        }
      />
      <EventEditor
        mode="edit"
        event={event}
        formId={formId}
        onSubmit={handleUpdateEvent}
      />
    </div>
  );
}
